'use client';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import AdminImageModal from '@wt/game/modals/ImageModal/AdminImageModal';
import MapElement from '@wt/shared/components/maps/mapkit/map';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit/events';
import { mapkitSearch } from '@wt/shared/libs/mapkit/util/mapkitSearch';
import { getSiteMode } from '@wt/utilities/siteMode';
import { ChangeEvent, memo, useEffect, useId, useMemo, useState } from 'react';
import Select, { MultiValue } from 'react-select';

import MapkitAutocomplete from '../../../shared/libs/mapkit/components/MapkitAutocomplete';
import {
  adminImageModalId,
  categories,
  imageUploadId,
  reactSelectStyles,
} from '../constants';
import { useImageModalOpen } from './providers/ImageModalOpenProvider';
import MapModal from '@wt/game/modals/MapModal';

const EditRoundForm = ({
  isPartOfGame,
  imageUrl,
  selectedImage,
  setSelectedImage,
  selectedLocation,
  handleChangeLocation,
  mapSearchInput,
  setMapSearchInput,
  year,
  setYear,
  selectedCategories,
  setSelectedCategories,
  description,
  setDescription,
  attribution,
  setAttribution,
  imageLink,
  setImageLink,
  photographer,
  setPhotographer,
  city,
  setCity,
  country,
  setCountry,
  roundIndex,
  filmName,
  setFilmName,
  yearBuffer,
  setYearBuffer,
  filmReleaseYear,
  setFilmReleaseYear,
  locationName,
  setLocationName,
}: {
  isPartOfGame: boolean;
  imageUrl: string;
  selectedImage: File | null;
  setSelectedImage: (e: File) => void;
  selectedLocation: mapkit.Coordinate | undefined;
  handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
  mapSearchInput: string;
  setMapSearchInput: (e: string) => void;
  year: string;
  setYear: (e: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (e: string[]) => void;
  description: string;
  setDescription: (e: string) => void;
  attribution: string;
  setAttribution: (e: string) => void;
  imageLink: string;
  setImageLink: (e: string) => void;
  photographer: string;
  setPhotographer: (e: string) => void;
  city: string;
  setCity: (e: string) => void;
  country: string;
  setCountry: (e: string) => void;
  filmName: string | undefined;
  setFilmName: (e: string | undefined) => void;
  yearBuffer: number | undefined;
  setYearBuffer: (e: number | undefined) => void;
  filmReleaseYear: number | undefined;
  setFilmReleaseYear: (e: number | undefined) => void;
  locationName: string | undefined;
  setLocationName: (e: string | undefined) => void;
  roundIndex: number;
}) => {
  const { imageModalOpen, setImageModalOpen } = useImageModalOpen();
  const imageSrc = selectedImage
    ? URL.createObjectURL(selectedImage)
    : imageUrl;
  const nonCachedImageSrc = selectedImage
    ? imageSrc
    : `${imageSrc}?${Date.now()}`;

  return (
    <div className="flex w-full flex-row justify-start gap-5">
      <div className="flex w-3/5 flex-col gap-3">
        {isPartOfGame && (
          <ImagePreview
            imageSrc={nonCachedImageSrc}
            setSelectedImage={setSelectedImage}
            openModal={() => setImageModalOpen(true, roundIndex)}
          />
        )}
        {!isPartOfGame && (
          <div className="relative aspect-[3/2] overflow-hidden rounded-lg">
            <img
              src={nonCachedImageSrc}
              alt={''}
              width="100%"
              height="100%"
              className="size-full object-cover object-center"
            />{' '}
          </div>
        )}
        <MapPreview
          selectedLocation={selectedLocation}
          handleChangeLocation={handleChangeLocation}
          mapSearchInput={mapSearchInput}
          setMapSearchInput={setMapSearchInput}
        />
      </div>
      <div className="flex w-2/5 flex-col gap-3">
        {getSiteMode() != 'wheretaken' && (
          <>
            <YearInput year={year} setYear={setYear} />
            {getSiteMode() == 'whentaken-movies' && (
              <GenericInput
                label="Year buffer"
                value={'' + (yearBuffer ?? 0)}
                setValue={(v: string) => {
                  setYearBuffer(+v);
                }}
              />
            )}
          </>
        )}
        {getSiteMode() != 'whentaken-movies' && (
          <CategoriesInput
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
          />
        )}
        {getSiteMode() == 'whentaken-movies' && (
          <GenericInput
            label="Film name"
            value={filmName ?? ''}
            setValue={setFilmName}
          />
        )}
        {getSiteMode() == 'whentaken-movies' && (
          <GenericInput
            label="Film release year"
            value={'' + (filmReleaseYear ?? 0)}
            setValue={(v: string) => {
              setFilmReleaseYear(+v);
            }}
          />
        )}
        <DescriptionInput
          description={description}
          setDescription={setDescription}
        />
        <GenericInput
          label={
            getSiteMode() == 'whentaken-movies' ? 'IMDb Link' : 'Image link'
          }
          value={imageLink}
          setValue={setImageLink}
        />
        {getSiteMode() != 'whentaken-movies' && (
          <GenericInput
            label="Photographer"
            value={photographer}
            setValue={setPhotographer}
          />
        )}
        <GenericInput label="City" value={city} setValue={setCity} />
        <GenericInput label="Country" value={country} setValue={setCountry} />
        {getSiteMode() == 'whentaken-movies' && (
          <GenericInput
            label="Location name"
            value={locationName ?? ''}
            setValue={setLocationName}
          />
        )}
      </div>
      <AdminImageModal
        id={adminImageModalId(nonCachedImageSrc)}
        src={nonCachedImageSrc}
        alt={''}
        open={imageModalOpen[roundIndex]}
        handleClose={() => setImageModalOpen(false, roundIndex)}
      />
    </div>
  );
};

export default EditRoundForm;

const ImagePreview = memo(
  ({
    imageSrc,
    setSelectedImage,
    openModal,
  }: {
    imageSrc: string;
    setSelectedImage: (e: File) => void;
    openModal: () => void;
  }) => {
    return (
      <div className="relative aspect-[3/2] overflow-hidden rounded-lg">
        <label onClick={openModal}>
          <img
            src={imageSrc}
            alt={''}
            width="100%"
            height="100%"
            className="size-full cursor-zoom-in object-cover object-center"
          />
        </label>
        <label
          htmlFor={imageUploadId(imageSrc)}
          className="absolute bottom-4 left-1/2 z-40 flex -translate-x-1/2 cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white p-2 px-3 text-sm text-base-100 hover:bg-white/70"
        >
          <FileUploadOutlinedIcon fontSize="small" />
          <span className="ml-2">Upload</span>
        </label>
        <input
          type="file"
          accept="image/*"
          id={imageUploadId(imageSrc)}
          className="hidden"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (e.target?.files && e.target.files.length) {
              setSelectedImage(e.target.files[0]);
            }
          }}
        />
      </div>
    );
  }
);

const MapPreview = memo(
  ({
    selectedLocation,
    handleChangeLocation,
    mapSearchInput,
    setMapSearchInput,
  }: {
    selectedLocation: mapkit.Coordinate | undefined;
    handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
    mapSearchInput: string;
    setMapSearchInput: (e: string) => void;
  }) => {
    return (
      <div className="relative aspect-[3/2] flex-1 overflow-hidden rounded-lg">
        <AdminMapElement
          selectedLocation={selectedLocation}
          handleChangeLocation={handleChangeLocation}
          mapSearchInput={mapSearchInput}
          setMapSearchInput={setMapSearchInput}
        />
        <MapModal
          id="map-modal"
          selectedLocation={selectedLocation}
          onLocationSelect={handleChangeLocation}
          label={' '}
          showSearchBar={true}
          mapSearchInput={mapSearchInput}
          setMapSearchInput={setMapSearchInput}
        />
      </div>
    );
  }
);

const AdminMapElement = memo(
  ({
    selectedLocation,
    handleChangeLocation,
    mapSearchInput,
    setMapSearchInput,
  }: {
    selectedLocation: mapkit.Coordinate | undefined;
    handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
    mapSearchInput: string;
    setMapSearchInput: (e: string) => void;
  }) => {
    const MapMemo = useMemo(() => {
      return (
        <MapElement
          selectedLocation={selectedLocation}
          onLocationSelect={handleChangeLocation}
          label={' '}
        />
      );
    }, [selectedLocation, handleChangeLocation]);

    return (
      <div className="rounded-border-with-shadow absolute size-full grow bg-[#8CD4F5]">
        {MapMemo}
        <label
          htmlFor={'map-modal'}
          className="absolute right-4 top-4  z-40  inline-flex size-8 cursor-pointer items-center justify-center rounded-lg border border-[#978FA3] bg-white shadow-lg shadow-black/20 hover:border-[#322E38] hover:bg-[#F6F0FE]"
        >
          <FullscreenRoundedIcon htmlColor="black" />
        </label>
        <MapSearchInput
          mapSearchInput={mapSearchInput}
          setMapSearchInput={setMapSearchInput}
          handleChangeLocation={handleChangeLocation}
        />
      </div>
    );
  }
);

export const MapSearchInput = ({
  mapSearchInput,
  setMapSearchInput,
  handleChangeLocation,
}: {
  mapSearchInput: string;
  setMapSearchInput: (e: string) => void;
  handleChangeLocation: (e: MapInteractionEvent | mapkit.Coordinate) => void;
}) => {
  const [items, setItems] = useState<mapkit.SearchAutocompleteResult[]>([]);

  useEffect(() => {
    mapkitSearch(mapSearchInput, setItems);
  }, [mapSearchInput]);

  return (
    <div className="absolute left-4 top-4 z-40 w-3/4">
      <MapkitAutocomplete
        items={items}
        value={mapSearchInput}
        onChange={(val) => setMapSearchInput(val)}
        onSelectLocation={handleChangeLocation}
      />
    </div>
  );
};

const YearInput = ({
  year,
  setYear,
}: {
  year: string;
  setYear: (e: string) => void;
}) => {
  return (
    <div className="flex w-full flex-col">
      <label htmlFor="year_input" className="mb-1 text-white">
        Year
      </label>
      <input
        id="year_input"
        type="text"
        inputMode="numeric"
        maxLength={4}
        placeholder="Year"
        className="input input-md w-full rounded-md border border-gray-300 bg-white text-black"
        value={year}
        onChange={(e) => setYear(e.target.value)}
      />
    </div>
  );
};

const CategoriesInput = ({
  selectedCategories,
  setSelectedCategories,
}: {
  selectedCategories: string[];
  setSelectedCategories: (e: string[]) => void;
}) => {
  const [localSelectedCategories, setLocalSelectedCategories] = useState<any[]>(
    []
  );
  const [localOptions, setLocalOptions] = useState<any[]>([]);

  useEffect(() => {
    setLocalSelectedCategories(
      selectedCategories.map((c) => {
        return {
          value: c,
          label: c,
        };
      })
    );

    setLocalOptions(
      categories.map((option) => {
        return {
          value: option,
          label: option,
        };
      })
    );
    return;
  }, [selectedCategories]);

  const handleChange = (
    newValue: MultiValue<{
      value: string;
      label: string;
    }>
  ) => {
    // Do not allow deleted KeyDate categories
    const alsoAdd = [];
    for (const v of localSelectedCategories) {
      if (v.label.startsWith('KeyDate-')) {
        const index = newValue.findIndex((n) => n.label === v.label);
        if (index === -1) {
          alsoAdd.push(v);
        }
      }
    }
    setLocalSelectedCategories([...newValue, ...alsoAdd]);
    setSelectedCategories([...newValue, ...alsoAdd].map((v) => v && v.label));
    return;
  };

  return (
    <div className="flex w-full flex-col">
      <label htmlFor="categories_input" className="mb-1 text-white">
        Categories
      </label>
      <Select
        className="rounded-md bg-white text-black"
        id="categories_input"
        closeMenuOnSelect={false}
        value={localSelectedCategories}
        isMulti
        options={localOptions}
        onChange={handleChange}
        isClearable={false}
        styles={reactSelectStyles}
        placeholder={'Select categories'}
      />
    </div>
  );
};

const DescriptionInput = ({
  description,
  setDescription,
}: {
  description: string;
  setDescription: (e: string) => void;
}) => {
  return (
    <div className="flex w-full flex-col">
      <label htmlFor="description_input" className="mb-1 text-white">
        Description
      </label>
      <textarea
        id="description_input"
        placeholder="Description"
        className="input input-md size-full rounded-md border border-gray-300 bg-white py-2.5 leading-tight text-black"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={5}
      />
    </div>
  );
};

export const GenericInput = ({
  label,
  value,
  setValue,
}: {
  label: string;
  value: string;
  setValue: (e: string) => void;
}) => {
  const id = useId();
  return (
    <div className="flex w-full flex-col">
      <label htmlFor={id} className="mb-1 text-white">
        {label}
      </label>
      <input
        id={id}
        type="text"
        placeholder={label}
        className="input input-md w-full rounded-md border border-gray-300 bg-white text-black"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
};
